import { jsx as _jsx } from "react/jsx-runtime";
import React, { useState, useEffect, useContext, createContext } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { setTheme as setReduxTheme } from "../../redux/theme-slice";
const initialState = {
    theme: "light",
    setTheme: () => null,
    isChangingTheme: false
};
const ThemeProviderContext = createContext(initialState);
// Helper function to get the system theme preference
const getSystemTheme = () => {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
    }
    return 'light';
};
export function ThemeProvider({ children, defaultTheme = "light", storageKey = "ui-theme", ...props }) {
    const dispatch = useDispatch();
    const reduxTheme = useSelector((state) => state.theme.current);
    const [theme, setThemeState] = useState(defaultTheme);
    const [isChangingTheme, setIsChangingTheme] = useState(false);
    useEffect(() => {
        const storedTheme = localStorage.getItem(storageKey);
        if (storedTheme) {
            setThemeState(storedTheme);
            dispatch(setReduxTheme(storedTheme));
        }
        else if (reduxTheme) {
            setThemeState(reduxTheme);
        }
    }, [dispatch, reduxTheme, storageKey]);
    useEffect(() => {
        const root = document.documentElement;
        root.classList.remove("light", "dark", "mellowYellow", "purpleRain", "blueSky");
        // If theme is system, apply the system preference
        if (theme === 'system') {
            const systemTheme = getSystemTheme();
            root.classList.add(systemTheme);
            root.setAttribute('data-theme', systemTheme);
        }
        else {
            root.classList.add(theme);
            root.setAttribute('data-theme', theme);
        }
        // Add theme-transition class to root to enable smooth transitions
        if (!root.classList.contains("theme-transition")) {
            root.classList.add("theme-transition");
        }
    }, [theme]);
    // Set up listener for system theme changes
    useEffect(() => {
        if (theme === 'system') {
            const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            const handleSystemThemeChange = (e) => {
                const root = document.documentElement;
                const newSystemTheme = e.matches ? 'dark' : 'light';
                // Update only the applied theme class, not the theme state
                root.classList.remove("light", "dark");
                root.classList.add(newSystemTheme);
                root.setAttribute('data-theme', newSystemTheme);
            };
            // Initial setup
            const systemTheme = getSystemTheme();
            document.documentElement.setAttribute('data-theme', systemTheme);
            // Add listener
            mediaQuery.addEventListener('change', handleSystemThemeChange);
            // Clean up
            return () => {
                mediaQuery.removeEventListener('change', handleSystemThemeChange);
            };
        }
    }, [theme]);
    const setTheme = (newTheme) => {
        // Prevent multiple rapid theme changes
        if (isChangingTheme)
            return;
        setIsChangingTheme(true);
        // Store in localStorage
        localStorage.setItem(storageKey, newTheme);
        // Update local state
        setThemeState(newTheme);
        // Update Redux state
        dispatch(setReduxTheme(newTheme));
        // Use requestAnimationFrame for better performance and smoother transitions
        requestAnimationFrame(() => {
            // Minimal repaint trigger
            void document.documentElement.offsetHeight;
        });
        // Reset changing state after transition completes (reduced to match button transition time)
        setTimeout(() => {
            setIsChangingTheme(false);
        }, 200); // Reduced to match faster button transitions
    };
    const value = {
        theme,
        setTheme,
        isChangingTheme,
    };
    return (_jsx(ThemeProviderContext.Provider, { ...props, value: value, children: children }));
}
export const useTheme = () => {
    const context = useContext(ThemeProviderContext);
    if (context === undefined)
        throw new Error("useTheme must be used within a ThemeProvider");
    return context;
};
